import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { usePagedArray, Pager } from 'rentalui-finance';
import {
  getInterBusinessCharges, payInterBusinessCharge, remindInterBusinessCharge, escalateInterBusinessCharge
} from '../api';

// shared_delivery_inter_business_billing.md - the two sides of inter-business delivery billing for
// one owner: what other businesses owe ME for using my shared delivery people, and what I owe
// other businesses for using theirs. The delivery person's own pay is a separate flow
// (OwnerPayablesPanel).
const STATUS_VARIANT = { pending: 'warning', escalated: 'info', paid: 'success', rejected: 'secondary' };
const rupees = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function LineItems({ items }) {
  if (!items?.length) return null;
  return (
    <ul className="small text-muted mb-0 ps-3">
      {items.map((li, i) => <li key={i}>{li.description || li.kind}: {rupees(li.amount)}</li>)}
    </ul>
  );
}

function PayModal({ charge, show, onHide, onPaid }) {
  const [method, setMethod] = useState('UPI');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (show) { setMethod('UPI'); setReference(''); setNote(''); setError(''); } }, [show]);
  if (!charge) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (reference.trim().length < 2) { setError('Enter the payment reference / transaction id.'); return; }
    setSubmitting(true);
    setError('');
    try {
      await payInterBusinessCharge(charge._id, { method, reference: reference.trim(), note: note.trim() });
      onPaid();
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to record this payment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton><Modal.Title as="h5">Pay {charge.resourceOwnerName || 'the other business'}</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-2">Order #{charge.bookingRef} &middot; <strong>{rupees(charge.amount)}</strong></p>
          {error && <Alert variant="danger" className="py-2">{error}</Alert>}
          <Form.Group className="mb-2">
            <Form.Label>Method</Form.Label>
            <Form.Select value={method} onChange={(e) => setMethod(e.target.value)}>
              {['UPI', 'BANK_TRANSFER', 'CASH', 'CARD', 'NET_BANKING', 'WALLET', 'OTHER'].map((m) => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Reference / transaction id <span className="text-danger">*</span></Form.Label>
            <Form.Control required minLength={2} maxLength={200} value={reference} onChange={(e) => setReference(e.target.value)} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Note (optional)</Form.Label>
            <Form.Control as="textarea" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>Cancel</Button>
          <Button variant="success" type="submit" disabled={submitting}>{submitting ? 'Saving…' : 'Confirm paid'}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default function InterBusinessChargesPanel() {
  const [owed, setOwed] = useState([]);
  const [owing, setOwing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [escalateFor, setEscalateFor] = useState(null);
  const [escalateNote, setEscalateNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [o, i] = await Promise.all([
        getInterBusinessCharges({ role: 'owed' }),
        getInterBusinessCharges({ role: 'owing' })
      ]);
      setOwed(o);
      setOwing(i);
    } catch {
      setError('Could not load inter-business charges.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remind = async (id) => {
    setBusyId(id);
    try { await remindInterBusinessCharge(id); await load(); } finally { setBusyId(null); }
  };
  const submitEscalate = async () => {
    setBusyId(escalateFor);
    try {
      await escalateInterBusinessCharge(escalateFor, escalateNote.trim());
      setEscalateFor(null);
      setEscalateNote('');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const owedPaged = usePagedArray(owed);
  const owingPaged = usePagedArray(owing);

  if (loading) return <Spinner animation="border" role="status" />;

  const totalOwed = owed.filter((c) => ['pending', 'escalated'].includes(c.status)).reduce((s, c) => s + c.amount, 0);
  const totalOwing = owing.filter((c) => ['pending', 'escalated'].includes(c.status)).reduce((s, c) => s + c.amount, 0);

  return (
    <div>
      {error && <Alert variant="danger">{error}</Alert>}
      <p className="text-muted small">
        When another business uses your shared delivery person (or you use theirs), the consuming
        business owes the resource-owning business an inter-business delivery fee. The delivery
        person's own pay is unaffected — see the Delivery payments tab.
      </p>

      <h6 className="mt-3">Owed to you <span className="text-muted">— {rupees(totalOwed)}</span></h6>
      {owedPaged.total === 0 ? <Alert variant="light">Nothing owed to you.</Alert> : (
        <>
        <Table responsive hover size="sm">
          <thead><tr><th>Order</th><th>From</th><th>Charges</th><th>Amount</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {owedPaged.rows.map((c) => (
              <tr key={c._id}>
                <td className="font-monospace">#{c.bookingRef}</td>
                <td>{c.consumingOwnerName || '—'}{c.consumingOwnerPhone && <div className="small text-muted">{c.consumingOwnerPhone}</div>}</td>
                <td><LineItems items={c.lineItems} /></td>
                <td>{rupees(c.amount)}</td>
                <td><Badge bg={STATUS_VARIANT[c.status] || 'secondary'}>{c.status}</Badge></td>
                <td className="text-nowrap">
                  {['pending', 'escalated'].includes(c.status) && (
                    <Button size="sm" variant="outline-secondary" className="me-1" disabled={busyId === c._id} onClick={() => remind(c._id)}>Remind</Button>
                  )}
                  {c.status === 'pending' && (
                    <Button size="sm" variant="outline-warning" disabled={busyId === c._id} onClick={() => { setEscalateFor(c._id); setEscalateNote(''); }}>Escalate</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Pager {...owedPaged} />
        </>
      )}

      <h6 className="mt-4">You owe <span className="text-muted">— {rupees(totalOwing)}</span></h6>
      {owingPaged.total === 0 ? <Alert variant="light">You don't owe any other business.</Alert> : (
        <>
        <Table responsive hover size="sm">
          <thead><tr><th>Order</th><th>To</th><th>Charges</th><th>Amount</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {owingPaged.rows.map((c) => (
              <tr key={c._id} className={c.status === 'escalated' ? 'table-warning' : undefined}>
                <td className="font-monospace">#{c.bookingRef}</td>
                <td>{c.resourceOwnerName || '—'}{c.resourceOwnerPhone && <div className="small text-muted">{c.resourceOwnerPhone}</div>}</td>
                <td><LineItems items={c.lineItems} /></td>
                <td>{rupees(c.amount)}</td>
                <td><Badge bg={STATUS_VARIANT[c.status] || 'secondary'}>{c.status}</Badge></td>
                <td>
                  {['pending', 'escalated'].includes(c.status) && (
                    <Button size="sm" variant="success" onClick={() => setPayFor(c)}>Mark paid</Button>
                  )}
                  {c.status === 'paid' && c.paymentReference && <span className="small text-muted">{c.paymentMethod} · {c.paymentReference}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Pager {...owingPaged} />
        </>
      )}

      {escalateFor && (
        <Alert variant="warning" className="mt-2">
          <div className="fw-semibold mb-1">Escalate this unpaid charge to the website owner?</div>
          <Form.Control as="textarea" rows={2} size="sm" className="mb-2" maxLength={1000}
            placeholder="Context (optional)" value={escalateNote} onChange={(e) => setEscalateNote(e.target.value)} />
          <Button size="sm" variant="warning" className="me-2" disabled={busyId === escalateFor} onClick={submitEscalate}>Escalate</Button>
          <Button size="sm" variant="outline-secondary" onClick={() => setEscalateFor(null)}>Cancel</Button>
        </Alert>
      )}

      <PayModal charge={payFor} show={!!payFor} onHide={() => setPayFor(null)} onPaid={() => { setPayFor(null); load(); }} />
    </div>
  );
}
